import {
  assembleDrawingDocument,
  collectDocumentHandlesDeep,
  defaultStyleTables,
  isHexHandle,
  isImportedSourceDocument,
  isLayoutBlockName,
  remapEntityHandleReferences,
  type DrawingDocument,
  type EntityColor,
  type Layout,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  editorShellStateSchema,
  type EditorShellState,
  type EntityPreview,
} from "@dxf-ai-editor/contracts";
import {
  buildShellEntityPreviewsFromGeometryDocument,
  buildShellSpacesFromGeometryDocument,
  countEntitiesAcrossSpaces,
  SHELL_ENTITY_PREVIEW_THRESHOLD,
  type CachedEntitiesLookup,
} from "./dxfGeometry";
import {
  enrichDimensionEntities,
  enrichMultileaderEntities,
} from "./propertyTranslator";
import { isEntityLevelNoOpMutation } from "./commitMutation";
// @codex #499 P2 — OPEN/import current-DIMSTYLE reconcile (initialState imports only leaves → acyclic).
import { reconcileCurrentDimStyleAgainstMergedDocument } from "./editorStoreInitialState";
import { useSpaceContextStore } from "./spaceContextStore";
import { getCachedEntitiesLookup } from "./cachedEntitiesRegistry";
import type { EntityMutation } from "./types";
// T7 (agent-bridge), file-size split — `buildMatchpropResolutionDeps` /
// `resolveGeometryCommandFromText` / `GeometryCreateCommand` /
// `parseLayoutMutationText` / `resolveLayoutMutationCommand` /
// `parseColorSetCommand` / `parseVpclipCommandText` /
// `resolveMatchpropApplyToPropertiesApply` moved OUT of this file's import
// block — they were used ONLY by `resolveGeometryDocumentCommand`, which now
// lives in `storeHelpersResolveCommand.ts` (re-exported below) and imports
// them itself. The dead `export { resolveMatchpropApplyToPropertiesApply }`
// barrel re-export that used to sit here had ZERO external consumers
// (verified by repo-wide grep — every match was a comment mention, not an
// import) and was removed rather than carried forward.

// T7 (agent-bridge), file-size split — the `cachedEntitiesLookup` registry
// slot moved to its own tiny module (`cachedEntitiesRegistry.ts`) so BOTH
// this file and `storeHelpersResolveCommand.ts` (which now owns
// `resolveGeometryDocumentCommand`, re-exported below) can read it without an
// import cycle between the two. Re-exported here so `editorStore.ts`'s
// existing `import { registerCachedEntitiesLookup } from "./storeHelpers"`
// keeps working unchanged (barrel re-export, no external import path churn —
// CLAUDE.md «Контроль размера файлов»).
export { registerCachedEntitiesLookup } from "./cachedEntitiesRegistry";

export function syncShellStateFromGeometryDocument(
  state: EditorShellState,
  geometryDocument: DrawingDocument,
  options?: {
    /**
     * Fast-path hint for style-CRUD flows that touch ONLY the style
     * tables (dimensionStyles / multileaderStyles / textStyles /
     * layers) without adding, removing, or reshaping any entity in the
     * geometry document. When true, skip the full
     * `buildShellEntityPreviewsFromGeometryDocument` pass (O(all
     * entities)) and reuse `state.drawing.entities` as-is; the
     * enrichment pipeline still runs so Dimension / Multileader
     * property bags pick up the new DIMVAR / MLVAR values from the
     * updated style tables.
     *
     * Only pass `true` when the caller has guaranteed that no
     * geometry entity has been added, removed, or had its fields
     * mutated in `geometryDocument` vs the geometry document the
     * shell state was last synced from. Examples:
     *   - `updateDimensionStyle` — only styleTables mutate. Safe.
     *   - `updateMultileaderStyle` / `addMultileaderStyle` — safe.
     *   - `addDimensionStyle` / `addMultileaderStyle` — safe.
     *   - `updateTextStyle` — safe.
     *   - `addLayer` / `updateLayer` — safe (layer attrs resolved at
     *     render time, not baked into previews).
     *   - `deleteDimensionStyle` / `renameDimensionStyle` / etc. —
     *     NOT safe: these REMAP entity.styleName in the geometry doc.
     *     Caller must pass `false` (or omit) so the full rebuild
     *     picks up the remapping.
     *   - `deleteLayer` — NOT safe: entity.layer gets reassigned.
     */
    skipEntityRebuild?: boolean;
    /**
     * Bypass the final `editorShellStateSchema.parse()` and return the
     * assembled object via cast. The Zod parse is an O(N) deep walk
     * over every entity field — on 195 MB DXFs with 685k entities it
     * blocks the main thread for ~30 sec, freezing the import dialog
     * at "Sync scene 90%". Bridge already validated each entity through
     * its own per-type Zod schema in `dxfBridge.import.ts`, so this
     * parse is redundant for files coming straight from the importer.
     *
     * Only pass `true` when the caller has guaranteed that the input
     * `geometryDocument` is itself a Zod-validated `DrawingDocument`
     * (true for `importGeometryDocumentFromDxfText` results, true for
     * `mergeImportedGeometryDocument(validated, validated)`). Other
     * callers (style CRUD, command reducers) MUST omit / pass false
     * because they may construct partial objects that rely on Zod
     * default-fillers in the parse step.
     */
    assumeValid?: boolean;
    /**
     * Phase 3b PR #6 — mutations payload from the geometry reducer.
     * When provided AND the mutations describe an ENTITY-level no-op (no
     * entity added/removed/updated), skip the expensive entity preview
     * rebuild — `state.drawing.entities` is known to mirror the geometry
     * document already.
     *
     * Lane J (D3-1) — the skip engages even when `styleTablesTouched` is
     * NON-empty (a layer-colour / DIMSTYLE / MLEADERSTYLE / text-style /
     * LTYPE edit): a style-table-only mutation reshapes NO entity, so the
     * previews stay valid. This is the O(1) fast-path the pre-Lane-J
     * direct-set setters got via `skipEntityRebuild: true` — routing them
     * through dispatch must not regress a style edit into an O(all-entities)
     * rebuild. The enrichment pipeline below still runs on the reused
     * previews, so the new DIMVAR / MLVAR values are projected.
     * `isEntityLevelNoOpMutation` is the shared "were entities touched?"
     * predicate; `isNoOpMutation` (the STRICTER "nothing at all changed") is
     * deliberately NOT used here — it forces a rebuild whenever a table was
     * touched.
     *
     * The surrounding shell state (activeSpaceId, spaces, styleTables
     * snapshot, layoutContext) IS still rebuilt because it can diverge
     * from `state.drawing.*` even when no entities mutated — e.g. BEDIT
     * enters/exits a block-edit space and the geo doc's activeSpaceId
     * rotates while the shell reducer hasn't touched its own copy.
     */
    mutations?: EntityMutation;
    // #518 — true when THIS dispatch moved `activeSpaceId` directly (BEDIT/BCLOSE/TESTBLOCK/REFEDIT; see editorStore.ts).
    geoActiveSpaceAuthoritative?: boolean;
  },
): EditorShellState {
  const geoDoc = geometryDocument;
  // Phase 3b PR #6 — an entity-no-op `mutations` payload reuses
  // `state.drawing.entities` instead of rebuilding from geometry. #518
  // (Codex P1) EXCEPT for a geo-authoritative space switch: its `space/*`
  // diff excludes synthetic spaces on both sides, so it always LOOKS
  // entity-no-op even though the STALE previous-space list needs rebuilding.
  const skipEntityRebuild =
    !options?.geoActiveSpaceAuthoritative &&
    (options?.skipEntityRebuild === true ||
      (options?.mutations !== undefined && isEntityLevelNoOpMutation(options.mutations)));

  // Build raw shell entities and run them through the same enrichment
  // pipeline as the dispatch loop. Without this both Dimension and
  // Multileader entities lose their style/MLVAR overrides on every
  // path that bypasses the reducer (e.g. DXF import or style CRUD).
  // The lookup is wired by editorStore via `registerCachedEntitiesLookup`
  // at module init — avoids the editorStore ↔ storeHelpers init cycle
  // without resorting to CommonJS `require` (which throws under ESM /
  // vitest because the package is `"type": "module"`).
  const cacheLookup: CachedEntitiesLookup = (spaceId: string) => {
    return getCachedEntitiesLookup()?.(spaceId);
  };
  // PR-V3 — for huge documents (entity count above
  // SHELL_ENTITY_PREVIEW_THRESHOLD), skip the per-entity preview build
  // entirely and leave `state.drawing.entities` empty. The
  // Scene3DStage cache-direct render path (PR-V2) reads from
  // `__entitiesCache` + `entityMetadataIndex`; UI panels (entity list,
  // properties palette) fall back to metadata aggregates / on-demand
  // `getEntityById(id)` lookups. Avoids the 340 MB transient preview
  // allocation on 685k-entity П35Р imports.
  let rawEntities: EntityPreview[];
  if (skipEntityRebuild) {
    rawEntities = state.drawing.entities;
  } else {
    const total = countEntitiesAcrossSpaces(geoDoc, cacheLookup);
    if (total > SHELL_ENTITY_PREVIEW_THRESHOLD) {
      rawEntities = [];
    } else {
      rawEntities = buildShellEntityPreviewsFromGeometryDocument(
        geoDoc,
        cacheLookup,
      );
    }
  }
  const dimEnriched = enrichDimensionEntities(
    rawEntities,
    geoDoc.styleTables?.dimensionStyles ?? [],
  );
  const enriched = enrichMultileaderEntities(
    dimEnriched,
    geoDoc.styleTables?.multileaderStyles ?? [],
  );

  // Phase 3b PR #6 — `activeSpaceId` resolution.
  //
  // Prefer the input `state.drawing.activeSpaceId` (set by upstream
  // shell reducer for `shell/activate-space`, `commandline/submit
  // "LAYOUT ..."`, etc.) when that space exists in the new geometry
  // document. Fall back to `geoDoc.activeSpaceId` only when the
  // shell's currently-active space disappeared (e.g. user deletes
  // a layout space from the layout manager).
  //
  // Why not always take `geoDoc.activeSpaceId`: the dispatch flow
  // is "shell first, geo second" — the shell reducer mutates the
  // shell's `activeSpaceId` during `LAYOUT` / `shell/activate-space`,
  // and the dispatch caller then writes `geoDoc.activeSpaceId =
  // resyncedState.drawing.activeSpaceId` AFTER this helper runs.
  // Overriding from the (older) `geoDoc.activeSpaceId` here would
  // erase the shell's just-set value before the writeback.
  // Why not always keep `state.drawing.activeSpaceId`: BEDIT /
  // refedit lifecycle commands (`space/enter-block-edit`, ...)
  // mutate the geo doc's `activeSpaceId` directly (synthetic
  // block-edit space) without routing through the shell reducer.
  // For those, taking `state.drawing.activeSpaceId` would leave
  // the shell pointing at the previous space; falling back to
  // geo for "shell space no longer exists" handles this — #518: BEDIT-enter
  // only supersedes (never removes) the shell's space, so that fallback misses it.
  const candidateActiveSpaceId = state.drawing.activeSpaceId;
  const candidateValid = geoDoc.spaces.some(
    (s) => s.id === candidateActiveSpaceId,
  );
  const resolvedActiveSpaceId =
    options?.geoActiveSpaceAuthoritative || !candidateValid
      ? geoDoc.activeSpaceId
      : candidateActiveSpaceId;

  const assembled = {
    ...state,
    drawing: {
      ...state.drawing,
      id: geoDoc.id,
      title: geoDoc.title,
      spaces: buildShellSpacesFromGeometryDocument(geoDoc),
      activeSpaceId: resolvedActiveSpaceId,
      layoutContext: (() => {
        // Block-edit (Phase 2.3) and block-test (DYN-1.9) spaces map to
        // the model layout context so the existing shell paths treat
        // them as editable canvases. The distinct space.kind still
        // drives block-specific UI (status bar indicator / tint) via
        // `geometryDocument.spaces` lookups.
        const activeSpace = geoDoc.spaces.find(
          (space) => space.id === resolvedActiveSpaceId,
        );
        const kind = activeSpace?.kind;
        if (kind !== "layout") {
          return "model";
        }
        // MC4 S2 P1 (iter-5 #1 + iter-6 #1, ROOT FIX) — preserve MSPACE_VIA_VP
        // across an in-VP selection, BUT let an explicit PSPACE/Paper exit win.
        //
        // When the user double-clicks a floating viewport, `enterViewport`
        // sets `useSpaceContextStore.mode = "MSPACE_VIA_VP"` while the
        // underlying `drawing.activeSpaceId` STAYS the layout space (the model
        // is edited *through* the VP, not via the Model tab). An in-VP pick
        // dispatches `shell/select-entities`, which DOES NOT touch
        // `drawing.layoutContext` — so the incoming `state.drawing.layoutContext`
        // is still "viewport" (set by the prior `enterViewport` mirror). This
        // helper would, before the fix, collapse it to "paper" for any
        // layout-kind active space; `App.tsx`'s `syncFromEditor` effect (keyed
        // on `[activeSpaceId, layoutContext]`) then maps that "paper" → PSPACE,
        // calls `exitViewport()`, and yanks the user OUT of the VP after the
        // first in-VP pick. The prior `reassertViewportSession` stopgap
        // re-entered the VP *after* the dispatch, but the next-render sync
        // still undid it.
        //
        // iter-6 #1 — the iter-5 guard fired on `mode === MSPACE_VIA_VP` ALONE,
        // which over-reached: an EXPLICIT exit (toolbar Paper button →
        // `shell/set-layout-context "paper"`, the PSPACE command, or a layout
        // tab switch via `shell/activate-space` with `layoutContext: "paper"`)
        // sets `state.drawing.layoutContext = "paper"` through the shell reducer
        // but does NOT exit the space-context store first (only the Esc path
        // calls `store.exitViewport()`). The iter-5 guard then rewrote that
        // explicit "paper" back to "viewport", trapping the user in the VP
        // (Paper/PSPACE appeared to do nothing — only Esc worked).
        //
        // Single-source fix: the INCOMING `state.drawing.layoutContext`
        // disambiguates the two paths. A selection/edit that KEEPS us in the VP
        // arrives with layoutContext already "viewport" (the shell sub-reducers
        // for `shell/select-entities` / `properties/apply` / geometry create
        // never mutate it). An explicit exit arrives with "paper" (or "model"),
        // because the shell reducer just set it. So we only re-assert "viewport"
        // when the caller did NOT explicitly transition out — i.e. when the
        // incoming layoutContext is itself "viewport". An explicit "paper" then
        // survives the resync, the App effect maps it to PSPACE, and
        // `syncFromEditor("paper")` exits the store — exactly mirroring Esc.
        // The guard stays scoped to the MSPACE_VIA_VP-with-valid-active-VP case.
        const spaceContext = useSpaceContextStore.getState();
        const incomingLayoutContext = state.drawing.layoutContext;
        if (
          incomingLayoutContext === "viewport" &&
          spaceContext.mode === "MSPACE_VIA_VP" &&
          spaceContext.activeViewportId !== null &&
          spaceContext.activeSpaceId === resolvedActiveSpaceId &&
          activeSpace?.entities.some(
            (entity) =>
              entity.type === "Viewport" &&
              entity.id === spaceContext.activeViewportId,
          )
        ) {
          return "viewport";
        }
        return "paper";
      })(),
      entities: enriched,
      styleTables: {
        textStyles: geoDoc.styleTables.textStyles.map((s) => ({
          name: s.name,
          fontName: s.fontName,
          height: s.height,
          widthFactor: s.widthFactor,
        })),
        dimensionStyles: geoDoc.styleTables.dimensionStyles.map((s) => ({
          name: s.name,
          textStyleName: s.DIMTXSTY,
          textHeight: s.DIMTXT,
          arrowheadSize: s.DIMASZ,
          overallScale: s.DIMSCALE,
          precision: s.DIMDEC,
        })),
        multileaderStyles: geoDoc.styleTables.multileaderStyles.map((s) => ({
          name: s.name,
          textStyleName: s.textStyleName,
          textHeight: s.textHeight,
          arrowheadSize: s.arrowheadSize,
          contentType: s.contentType,
        })),
      },
    },
    // Phase 3b PR #6 — preserve `propertyWorkflows` (including
    // `matchpropSourceEntityId`) from the input state instead of
    // resetting `matchpropSourceEntityId: null`. With unconditional
    // resync, wiping the matchprop source on every dispatch would
    // break the matchprop/set-source → matchprop/apply two-step flow
    // because the second step would never see the source from the
    // first. The shell sub-reducer (`reducePropertyCommand`) already
    // manages this field on every relevant command.
    propertyWorkflows: state.propertyWorkflows,
    // Phase 3b PR #6 — preserve `state.selection` instead of wiping
    // to `entityIds: []`. Selection management is the responsibility
    // of the shell sub-reducer (or upstream caller); this helper now
    // owns the geometry → shell translation and should never silently
    // drop selection.
    //
    // Before PR #6 the dispatch flow was gated by the
    // `needsShellResyncFromGeometry` allowlist — commands NOT in the
    // list (e.g. `geometry/create-multileader`) skipped this helper
    // entirely, so the shell sub-reducer's selection survived. With
    // unconditional resync (PR #6) the helper is on the hot path of
    // every dispatch, so wiping selection would silently drop the
    // user's selection on every CHPROP / properties/apply / create-*
    // command. Style-CRUD callers (addDimensionStyle, addLayer, ...)
    // already wrap this helper with `selection: priorSelection`
    // overrides; preserving here is therefore a strict superset of
    // the old contract and a fix for the dispatch flow.
    selection: state.selection,
  };

  // Fast path for huge documents (DXF importer): bypass the Zod
  // walk over every entity. See `assumeValid` JSDoc above for the
  // safety contract. On 685k-entity files this saves ~30 sec of
  // main-thread blocking that would otherwise freeze the import
  // dialog at "Sync scene 90%".
  if (options?.assumeValid) {
    return assembled as EditorShellState;
  }
  return editorShellStateSchema.parse(assembled);
}

export function isGeometryBackedPreviewEntity(entity: EntityPreview): boolean {
  return (
    entity.type === "Line" ||
    entity.type === "Circle" ||
    entity.type === "Arc" ||
    entity.type === "Ellipse" ||
    entity.type === "Spline" ||
    entity.type === "LWPOLYLINE" ||
    entity.type === "Text" ||
    entity.type === "MText" ||
    entity.type === "Dimension" ||
    entity.type === "Multileader" ||
    entity.type === "Revision Cloud" ||
    entity.type === "Wipeout" ||
    entity.type === "Hatch" ||
    entity.type === "Block" ||
    entity.type === "Rectangular Associative Array" ||
    entity.type === "Viewport"
  );
}

/**
 * Merge rule for a document-level sysvar whose AutoCAD default is 0
 * ($INSUNITS / $PDMODE / $PDSIZE). The current document at import time is
 * typically the empty-doc seed carrying the schema default (0). The synchronous
 * `importDxfText` path ALWAYS merges into that seed, so without this rule the
 * `...currentDocument` spread would clobber the imported value back to the seed
 * default and the value would be dropped before export (Codex #248 F3).
 *
 * Rule (mirrors the original `$INSUNITS` special-case): take the imported value
 * only when the current is the default (0) AND the imported is non-default
 * (!= 0); otherwise keep current. This lets a real drawing's sysvar flow into
 * an empty document while never letting a unitless / default-0 import wipe a
 * value the user already has.
 */
function mergeDefaultZeroVar(current: number, imported: number): number {
  return current === 0 && imported !== 0 ? imported : current;
}

/**
 * Merge rule for a document-level sysvar whose AutoCAD default is NON-zero
 * ($OSMODE default = 4133 = ENDPOINT|MIDPOINT|CENTER|INTERSECTION). Identical
 * shape to {@link mergeDefaultZeroVar} but parameterised on the default so a
 * legitimately-imported value EQUAL to the default is not mistaken for "unset".
 *
 * The empty-doc seed carries the schema default (4133), so the `...currentDocument`
 * spread would otherwise clobber an imported $OSMODE (e.g. 191, or a suppressed
 * 20583) back to 4133 — dropped before export and then projected as a stale
 * default over the file's value on the first save (Codex #342 F3). $OSMODE could
 * NOT reuse `mergeDefaultZeroVar` because its default is 4133, not 0: that helper
 * keeps `current` whenever `current !== 0`, so a 4133-seed always wins over the
 * import. Rule: take the imported value only when the current is the default AND
 * the imported differs from the default; otherwise keep current (so a user value
 * already set is never wiped by a default import).
 */
function mergeDefaultVar(
  current: number,
  imported: number,
  defaultValue: number,
): number {
  return current === defaultValue && imported !== defaultValue
    ? imported
    : current;
}

/** AutoCAD $OSMODE fresh-install default (ENDPOINT|MIDPOINT|CENTER|INTERSECTION). */
const OSMODE_DEFAULT = 4133;

/**
 * Merge rule for $CECOLOR — the typed `EntityColor` UNION, so it CANNOT reuse
 * the numeric `mergeDefaultZeroVar` / `mergeDefaultVar` helpers. Same SHAPE
 * though: the empty-doc seed carries the schema default (ByLayer), and the
 * synchronous `importDxfText` path always merges into that seed, so without this
 * rule the `...currentDocument` spread would clobber the imported $CECOLOR back
 * to ByLayer and the value would be dropped before export + hydration.
 *
 * Rule (Codex #343 round-3 FINDING C — empty-seed ALWAYS adopts the imported
 * value verbatim, incl. a ByLayer reset): take the imported colour whenever the
 * current document is a genuine EMPTY SEED (a fresh OPEN); otherwise PRESERVE
 * the current colour (a real INSERT into a live drawing).
 *
 * Round-2 (FINDING 1) added the EXPLICIT empty-seed gate replacing the prior
 * `isByLayerColor(current)` proxy, but ALSO guarded on `!isByLayerColor(imported)`
 * — which broke the empty-OPEN of a file with ABSENT/default $CECOLOR (imported =
 * ByLayer): the guard returned the STALE prior current colour, so a user who
 * changed CECOLOR in an empty session then opened a no-$CECOLOR file would have
 * that stale colour written into a file that had no $CECOLOR. Dropping the
 * `!isByLayerColor(imported)` guard makes the empty OPEN adopt the file header
 * WHOLE (incl. the ByLayer reset). The prior code used `isByLayerColor(current)`
 * as a proxy for "empty seed", which wrongly adopted the inserted file's $CECOLOR
 * on an INSERT into a NON-empty drawing whose live current colour merely happened
 * to still be ByLayer — even though the live `cecolorStore` is intentionally NOT
 * hydrated on INSERT (see `editorStore.hydrateCeColorFromImportedDocument`, gated
 * on the SAME empty-seed flag). Mirroring the $MIRRTEXT / $OSMODE hydration gate:
 * empty-seed OPEN adopts the imported value verbatim, a real INSERT keeps the
 * host doc's $CECOLOR.
 *
 * `currentIsEmptySeed` is supplied by the caller (the authoritative
 * `isDocumentEffectivelyEmpty` predicate — inline entities OR metaIndex.size OR
 * the cache-populated sticky flag). It is OPTIONAL with a pure entity-presence
 * default (`every space has no entities`) so the unit-test callers that omit it
 * keep the correct OPEN-vs-INSERT classification without the cache/index reach.
 */
function mergeCeColor(
  current: DrawingDocument,
  imported: EntityColor,
  currentIsEmptySeed: boolean,
): EntityColor {
  // Empty-seed OPEN adopts the imported file header WHOLE (incl. a ByLayer reset
  // when the file has no/default $CECOLOR — FINDING C); a real INSERT into a live
  // drawing preserves the host doc's current colour (round-2 FINDING 1 intact).
  return currentIsEmptySeed ? imported : current.cecolor;
}

/**
 * FIX 2 (P1, host data loss) — the cache-INDEPENDENT structural portion of the
 * fresh-seed-vs-host predicate, SHARED with editorStore's
 * `isDocumentEffectivelyEmpty` (which adds the non-reactive cache + metadata-index
 * signals on top). ONE source of truth so the merge-bypass classification cannot
 * diverge between the two files.
 *
 * Returns TRUE iff the document carries NO non-entity host state that the merge
 * path would otherwise UNION but the REPLACE branch would DROP: a USER block
 * record / block definition (any name not among the reserved `*Model_Space` /
 * `*Paper_Space` records), any LAYOUT, annotation scale, xref, the
 * `provenance:"imported"` marker, a USER symbol-table entry (a text / dim /
 * multileader style not named "Standard", or a layer not named "0"), OR an EDITED
 * DEFAULT record (a layer "0" / "Standard" style whose VALUES differ from the
 * fresh-seed defaults — BUG 4). A pristine `buildInitialGeometryDocument` seed
 * (empty maps, `annotationScales: []`, untouched default styleTables) returns TRUE.
 */
export function documentHasNoNonEntityHostState(doc: DrawingDocument): boolean {
  // round-17 BUG 1 (per-SITE sweep) — an IMPORTED-SOURCE doc (marker OR raw SECTION
  // source) is never a fresh seed; the narrow `provenance === "imported"` let a
  // MARKER-DROPPED raw-section host be mis-read as a fresh seed (raw HEADER/TABLES/
  // OBJECTS dropped on the REPLACE/bypass branch). See storeHelpers.round17.test.ts.
  if (isImportedSourceDocument(doc)) return false;
  if (
    Object.keys(doc.blockRecords ?? {}).some((n) => !isLayoutBlockName(n))
  ) {
    return false;
  }
  if (Object.keys(doc.blocks ?? {}).some((n) => !isLayoutBlockName(n))) {
    return false;
  }
  if (Object.keys(doc.layouts ?? {}).length > 0) return false;
  if ((doc.annotationScales?.length ?? 0) > 0) return false;
  if (Object.keys(doc.xrefRegistry ?? {}).length > 0) return false;
  const st = doc.styleTables;
  if (st) {
    // BUG 4 (round-11 P2) — a default-named symbol-table record that was EDITED
    // (layer "0" recolored, "Standard" text/dim/mleader style with a changed
    // font / dimvars) is HOST STATE the merge UNIONs but the REPLACE branch would
    // DROP. The prior check compared only NAMES, so an edited DEFAULT was
    // mis-classified as a fresh seed → its host edits were silently lost on the
    // import replace/bypass branch. `tableHasHostState` returns true when EITHER a
    // non-default-named record exists OR a default-named record's VALUES diverge
    // from the fresh-seed default (`defaultStyleTables`, the single source of truth
    // for the seed). The `handle` field is INCIDENTAL (assigned on import / mint,
    // never a host edit) so it is ignored in the value comparison; the SACRED guard
    // holds — a pristine seed record equals its default → returns true (no edits).
    if (
      tableHasHostState(st.textStyles, defaultStyleTables.textStyles, "Standard") ||
      tableHasHostState(
        st.dimensionStyles,
        defaultStyleTables.dimensionStyles,
        "Standard",
      ) ||
      tableHasHostState(
        st.multileaderStyles,
        defaultStyleTables.multileaderStyles,
        "Standard",
      ) ||
      tableHasHostState(st.layers, defaultStyleTables.layers, "0")
    ) {
      return false;
    }
  }
  return true;
}

/**
 * BUG 4 (round-11 P2) — return TRUE when a symbol-table `entries` array carries
 * host state vs the fresh seed: EITHER a record whose name is NOT the default
 * name (a USER style/layer), OR the default-named record's VALUES differ from the
 * fresh-seed default `seedDefaults[name]`. The incidental `handle` field (minted
 * on import, never a host edit) is stripped before the value compare so a seed
 * record that merely gained a handle is not mistaken for an edit. `seedDefaults`
 * carries exactly one record (the default), so we look it up by `defaultName`.
 */
function tableHasHostState<T extends { name: string }>(
  entries: T[] | undefined,
  seedDefaults: T[] | undefined,
  defaultName: string,
): boolean {
  const list = entries ?? [];
  const seedDefault = (seedDefaults ?? []).find((e) => e.name === defaultName);
  for (const entry of list) {
    if (entry.name !== defaultName) return true; // a USER (non-default) record.
    // An EDITED default — compare values (ignoring the incidental `handle`).
    if (seedDefault && !recordsEqualIgnoringHandle(entry, seedDefault)) {
      return true;
    }
  }
  return false;
}

/**
 * Structural equality of two symbol-table records ignoring the incidental
 * `handle` field (BUG 4 value comparison). Deep-compares via a stable JSON
 * serialization of the records with `handle` stripped — the records are plain
 * data (numbers / strings / booleans / nested style objects), so JSON round-trip
 * is a sound deep-equality oracle here (no functions / Dates / cycles).
 */
function recordsEqualIgnoringHandle(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const strip = (r: Record<string, unknown>): Record<string, unknown> => {
    const { handle: _handle, ...rest } = r;
    return rest;
  };
  return stableStringify(strip(a)) === stableStringify(strip(b));
}

/**
 * Deterministic JSON stringify with sorted object keys at every depth so two
 * structurally-equal records serialize identically regardless of key insertion
 * order (a host edit re-creates the record; key order can differ from the seed).
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableStringify(v)).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`;
}

/**
 * Pure entity-presence + non-entity-host-state subset of
 * `isDocumentEffectivelyEmpty` — the DEFAULT empty-seed signal for the merge
 * helpers when the caller omits the explicit flag (test-only callers). The real
 * store callers pass the authoritative `isDocumentEffectivelyEmpty` (which also
 * consults the non-reactive cache + metadata index for a streamed-OPEN document).
 * A document with any inline entity OR any non-entity host state (block / layout /
 * style / scale / xref / imported marker) is unambiguously a non-empty host.
 */
function documentHasNoInlineEntities(doc: DrawingDocument): boolean {
  return (
    doc.spaces.every((s) => s.entities.length === 0) &&
    documentHasNoNonEntityHostState(doc)
  );
}

/**
 * BUG 1 (round-11 P2) — a fresh, unique entity id for a collision-rename during an
 * INSERT merge. The id KIND depends on the HOST's provenance:
 *
 *   - HOST IMPORTED → the host doc is passed THROUGH verbatim by
 *     `ensureConformantSkeleton` (it early-returns on `provenance:"imported"`), so
 *     `entity.id` is emitted AS the DXF group-5 handle. A non-hex collision id
 *     (`"line-1"`) is then a HARD strict-ezdxf `Invalid handle` reject. So the
 *     rename must mint a fresh HEX id, collision-checked against `usedEntityIds`.
 *   - HOST FROM-SCRATCH → `ensureConformantSkeleton` RE-MINTS every `entity.id` to
 *     hex anyway, so the legacy human-readable `"${type}-${n}"` id is harmless and
 *     KEPT (the broad "non-hex-for-all-draws" rework stays deferred per pt-k; this
 *     fixes ONLY the narrow imported-host collision case).
 *
 * The HEX path mints from a high base (`0x100000+`, matching the conformant-skeleton
 * entity re-mint range) so a minted hex id is visibly distinct from the low
 * structural handles, and re-probes `usedEntityIds` so two collisions never alias.
 */
function mintCollisionRenameId(
  collidingType: string,
  usedEntityIds: ReadonlySet<string>,
  hostIsImported: boolean,
): string {
  if (hostIsImported) {
    // Mint a fresh HEX id unique within the merged id population. 0x100000 is the
    // conformant-skeleton entity re-mint base; a collision against a copied source
    // handle re-probes upward. DXF handles are CASE-INSENSITIVE (the canonical
    // `makeHandleMinter` probes/stores LOWERCASED), and the BUG-2 structural seed
    // (`collectHandlesDeep`) stores handles LOWERCASED — so probe both the verbatim
    // candidate AND its lowercase form, else a minted `"10000A"` would slip past a
    // seeded `"10000a"` structural handle → duplicate group-5.
    let seq = 0x100000;
    let candidate = seq.toString(16).toUpperCase();
    while (
      usedEntityIds.has(candidate) ||
      usedEntityIds.has(candidate.toLowerCase()) ||
      !isHexHandle(candidate)
    ) {
      seq += 1;
      candidate = seq.toString(16).toUpperCase();
    }
    return candidate;
  }
  // From-scratch host — keep the legacy human-readable id (re-minted to hex later).
  const prefix = collidingType.toLowerCase();
  let n = 1;
  let candidate = `${prefix}-${n}`;
  while (usedEntityIds.has(candidate)) {
    n += 1;
    candidate = `${prefix}-${n}`;
  }
  return candidate;
}

/**
 * BUG 2 (round-12 P2) — seed `usedEntityIds` with the FULL host handle population
 * (not just `spaces[].entities` ids) when the merged result will be emitted as an
 * IMPORTED (pass-through) document.
 *
 * For an imported host the conformant skeleton early-returns (`provenance:"imported"`)
 * and each `entity.id` is emitted VERBATIM as a group-5 handle. But the host ALSO
 * carries STRUCTURAL handles — blockRecords, blocks, layouts, style/layer tables,
 * and the verbatim raw streams (`rawTableGroups`/`rawObjectGroups`/`rawPassthrough
 * Sections`/`rawEntityGroups`, whose `{code:5|105|1005}` leaves are own/ref handles).
 * The 0x100000-range collision-rename hex (`mintCollisionRenameId`) probed ONLY the
 * entity ids, so a minted hex could ALIAS a structural host handle in that range →
 * DUPLICATE group-5 → strict-ezdxf reject. Reuse the SAME deep handle census the
 * conformant skeleton uses (`collectHandlesDeep`) so the renamed hex avoids EVERY
 * existing host handle. The entity-id walk in each merge fn already covers
 * `spaces[].entities`; this adds the non-entity structural population.
 */
/**
 * T9b review round 3/4 — the structural seed walks the SHARED document handle
 * census (`collectDocumentHandlesDeep`, drawing-model): the one partition of
 * `drawingDocumentSchema` into handle-bearing / handle-free fields, used by
 * every minter (the store's collision-rename, the imported-document export
 * preparation). A hand-picked list here once missed `linetypes` (the
 * name-keyed LTYPE table) → a mint aliased an LTYPE record handle. The two
 * lists are re-exported for the census test.
 */
export {
  DOCUMENT_HANDLE_FIELDS as STRUCTURAL_HOST_HANDLE_FIELDS,
  HANDLE_FREE_DOCUMENT_FIELDS,
} from "@dxf-ai-editor/drawing-model";

function seedStructuralHostHandles(
  host: DrawingDocument,
  usedEntityIds: Set<string>,
): void {
  // The merge functions walk `spaces[].entities` ids themselves (an imported
  // batch's own ids must not read as collisions against itself).
  collectDocumentHandlesDeep(host, usedEntityIds, { entityIds: false });
}

/**
 * Defect #9 (marker⇔raw invariant) — a document marked `provenance:"imported"`
 * MUST carry at least one non-empty verbatim raw stream
 * (`rawPassthroughSections` / `rawTableGroups` / `rawObjectGroups`). The
 * conformant-skeleton from-scratch detector treats the marker as authoritative
 * "imported ⇒ pass-through, do NOT back-fill the skeleton"; if a merge/open path
 * stamps the marker but DROPS the imported raw streams (the #384 SYNC-OPEN bug),
 * the next save skips the skeleton AND has no raw to carry-forward → it emits a
 * non-conformant minimal DXF (no HEADER/tables/OBJECTS, non-hex ids).
 *
 * Returns `true` when the invariant HOLDS (not imported, OR imported with ≥1 raw
 * stream). A regression that re-introduces the drop fails the storeHelpers test
 * that asserts this on the merge/open result — it never ships a bad DXF.
 */
export function importedMarkerImpliesRaw(doc: DrawingDocument): boolean {
  if (doc.provenance !== "imported") return true;
  return (
    (doc.rawPassthroughSections?.length ?? 0) > 0 ||
    (doc.rawTableGroups?.length ?? 0) > 0 ||
    (doc.rawObjectGroups?.length ?? 0) > 0
  );
}

/**
 * Phase 1.b (П35Р) — async chunked variant of
 * `mergeImportedGeometryDocument`. Yields `setTimeout(0)` every
 * `chunkSize` entities while walking `importedDocument.spaces[].entities`.
 *
 * The merge has two O(N) passes: building `usedEntityIds` from current
 * doc, and walking imported entities (collision-rename + push to
 * target). For 685k-entity drawings this is ~1-3s sync work — long
 * enough for `page.evaluate` probes to time out + scene animation
 * frames to drop.
 *
 * Final `assembleDrawingDocument(...)` stays sync (Zod parse / model
 * invariants check is bounded; <250ms on 685k entries per existing
 * perf budget).
 */
export async function mergeImportedGeometryDocumentAsync(
  currentDocument: DrawingDocument,
  importedDocument: DrawingDocument,
  chunkSize = 2000,
  // Codex #343 round-1 FINDING 1 — explicit empty-seed gate for the $CECOLOR
  // merge (mirrors the $MIRRTEXT/$OSMODE hydration gate). The store callers
  // reach this fn ONLY on a true INSERT (the empty-seed OPEN branch bypasses
  // merge entirely with the imported doc whole), so they pass `false`. Optional
  // with a pure entity-presence default for the test callers.
  currentIsEmptySeed: boolean = documentHasNoInlineEntities(currentDocument),
): Promise<DrawingDocument> {
  const mergedBlocks = {
    ...(currentDocument.blocks ?? {}),
    ...(importedDocument.blocks ?? {}),
  };
  const mergedBlockRecords = {
    ...(currentDocument.blockRecords ?? {}),
    ...(importedDocument.blockRecords ?? {}),
  };
  const mergedLayouts = {
    ...(currentDocument.layouts ?? {}),
    ...(importedDocument.layouts ?? {}),
  };
  const mergedStyleTables = (() => {
    const cur = currentDocument.styleTables ?? {};
    const imp = importedDocument.styleTables ?? {};
    return {
      ...cur,
      ...imp,
      dimensionStyles: dedupeStylesByName(cur.dimensionStyles, imp.dimensionStyles),
      textStyles: dedupeStylesByName(cur.textStyles, imp.textStyles),
      multileaderStyles: dedupeStylesByName(cur.multileaderStyles, imp.multileaderStyles),
    };
  })();
  const mergedInsunits = mergeDefaultZeroVar(
    currentDocument.insunits,
    importedDocument.insunits,
  );
  const mergedPdmode = mergeDefaultZeroVar(
    currentDocument.pdmode,
    importedDocument.pdmode,
  );
  const mergedPdsize = mergeDefaultZeroVar(
    currentDocument.pdsize,
    importedDocument.pdsize,
  );
  // $MIRRTEXT follows the SAME default-0 merge rule as $INSUNITS/$PDMODE — the
  // `...currentDocument` spread starts from the empty seed (mirrtext=0), so
  // without this the imported document's MIRRTEXT would be dropped before
  // export (and the App-level sysvar hydration that reads doc.mirrtext after
  // import would see 0 instead of the file's value).
  const mergedMirrtext = mergeDefaultZeroVar(
    currentDocument.mirrtext,
    importedDocument.mirrtext,
  );
  // $OSMODE merge — SAME story as $MIRRTEXT, but its default is 4133 (non-zero),
  // so it uses the parameterised `mergeDefaultVar`. Without this the imported
  // $OSMODE (e.g. 191, or a 16384-suppressed 20583) is dropped before hydration
  // and the first save projects the stale 4133 default over the file (#342 F3).
  const mergedOsmode = mergeDefaultVar(
    currentDocument.osmode,
    importedDocument.osmode,
    OSMODE_DEFAULT,
  );
  // $CECOLOR — typed union, same default-aware merge as the numeric sysvars
  // above but for `EntityColor`: an empty-seed OPEN takes the imported current
  // colour, an INSERT into a live drawing keeps the live colour (no wipe). Gated
  // on the EXPLICIT empty-seed flag (FINDING 1), not the ByLayer-current proxy.
  const mergedCeColor = mergeCeColor(
    currentDocument,
    importedDocument.cecolor,
    currentIsEmptySeed,
  );

  const spacesById = new Map<string, (typeof currentDocument.spaces)[number]>();
  for (const space of currentDocument.spaces) {
    spacesById.set(space.id, { ...space, entities: [...space.entities] });
  }
  const usedEntityIds = new Set<string>();
  // Pass 1: build seen-id set from current document (small for OPEN —
  // empty seed; non-trivial only on INSERT into an already-loaded
  // drawing). BUG 4 (round-14 P2) — seed LOWERCASED so the collision probe is
  // case-insensitive (DXF handles are case-insensitive; an existing host "ABC"
  // and an inserted "abc" are the same handle → the inserted one must be
  // renamed). Mirrors the sync path + the lowercased structural seed.
  for (const space of spacesById.values()) {
    let count = 0;
    for (const e of space.entities) {
      usedEntityIds.add(e.id.toLowerCase());
      count++;
      if ((count & (chunkSize - 1)) === 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
    }
  }

  // BUG 1 (round-11 P2) — a collision-rename must mint a HEX id when the merged
  // result is emitted as IMPORTED (pass-through): the writer emits `entity.id`
  // verbatim as group-5, so a non-hex `"line-1"` is a strict-ezdxf reject.
  // round-17 BUG 1 (per-SITE sweep) — gate on `isImportedSourceDocument` (marker OR
  // raw SECTION source) so a MARKER-DROPPED raw-section host (passed through by the
  // skeleton) takes the HEX-mint + structural-seed branch. See round17 test.
  const resultIsImported =
    currentIsEmptySeed || isImportedSourceDocument(currentDocument);

  // BUG 2 (round-12 P2) — when the result is emitted as IMPORTED (pass-through),
  // the minted hex collision-rename id is emitted VERBATIM as a group-5 handle, so
  // it must avoid EVERY existing handle — not just the entity ids seeded above, but
  // the STRUCTURAL host handles (blockRecords/blocks/layouts/styleTables/raw
  // groups) of BOTH the merged-in source AND the host. Seed those into the same
  // avoidance set so a 0x100000-range mint never aliases a structural handle.
  if (resultIsImported) {
    seedStructuralHostHandles(currentDocument, usedEntityIds);
    seedStructuralHostHandles(importedDocument, usedEntityIds);
  }

  // round-16 BUG 1 — accumulate every collision-rename as oldId(lower) → newId so
  // SIBLINGS in the SAME imported batch that referenced the OLD id (a HATCH
  // `sourceBoundaryHandles`, a viewport `clippingBoundaryHandle`, an MLEADER
  // `attDefHandle`, a dimension/reactor ref, an XData 1005) can FOLLOW the rename
  // after the walk. Without this the refs dangle or resolve to the host's
  // same-spelled handle. The map values are typed entity IDS (matching how these
  // refs are stored pre-export), so a single `remapEntityHandleReferences` pass
  // (the SAME field set `rewriteEntityHandlesGeneric` / `collectHandlesDeep` walk)
  // closes the whole sibling-ref set by construction. We rewrite ONLY the IMPORTED
  // entities (collected here), never the HOST entities already in `target.entities`
  // — a host entity that references the colliding id legitimately points at the
  // host's OWN entity and must NOT be re-homed onto the renamed imported one.
  const importedRenameMap = new Map<string, string>();
  const importedEntitiesForRemap: DrawingDocument["spaces"][number]["entities"][number][] =
    [];
  // Pass 2: walk imported, collision-rename if needed, push to target.
  // For 685k-entity OPEN this is the dominant cost; yield every
  // chunkSize so the probe + rAF can fire between batches.
  for (const importedSpace of importedDocument.spaces) {
    const target =
      spacesById.get(importedSpace.id) ??
      (() => {
        const fresh = { ...importedSpace, entities: [] };
        spacesById.set(importedSpace.id, fresh);
        return fresh;
      })();
    let i = 0;
    for (const importedEntity of importedSpace.entities) {
      let entity = importedEntity;
      // BUG 3 (round-13) + BUG 4 (round-14) — case-insensitive collision probe
      // (mirrors the sync path). The avoidance set is now FULLY case-normalized:
      // `seedStructuralHostHandles` seeds structural handles LOWERCASED AND the
      // existing host entity ids are seeded lowercased above. Probe the lowercase
      // id so an imported "10000A" cannot slip past a seeded "10000a" structural
      // handle NOR an existing host entity id "ABC" → no duplicate group-5.
      const idLower = importedEntity.id.toLowerCase();
      if (usedEntityIds.has(idLower)) {
        const candidate = mintCollisionRenameId(
          importedEntity.type,
          usedEntityIds,
          resultIsImported,
        );
        entity = { ...importedEntity, id: candidate };
        // round-16 BUG 1 — record old→new so same-batch sibling refs follow.
        importedRenameMap.set(idLower, candidate);
      }
      // Re-add the kept/renamed id LOWERCASED so the set stays case-normalized.
      usedEntityIds.add(entity.id.toLowerCase());
      target.entities.push(entity);
      importedEntitiesForRemap.push(entity);
      i++;
      if ((i & (chunkSize - 1)) === 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
    }
  }
  // round-16 BUG 1 — rewrite each IMPORTED entity's reference fields through the
  // accumulated rename map so a sibling that referenced a renamed id resolves to
  // its NEW id (never the OLD dangling id, never the host's same-spelled handle).
  // Guarded on a non-empty map (no rename ⇒ no-op). The own `id` is excluded by
  // `remapEntityHandleReferences` (the bare own-handle identity is never remapped),
  // so a renamed entity's own id is untouched.
  if (importedRenameMap.size > 0) {
    for (const entity of importedEntitiesForRemap) {
      remapEntityHandleReferences(entity, importedRenameMap);
    }
  }

  const importedHasScales =
    Array.isArray(importedDocument.annotationScales) &&
    importedDocument.annotationScales.length > 0;
  const mergedAnnotationScales = importedHasScales
    ? importedDocument.annotationScales
    : // `?? []` — a host with no annotation catalogue (undefined) must not crash
      // the `.some` membership probe below. Previously masked because the import
      // left an EMPTY cannoscaleName (falsy → `&&` short-circuited the probe); the
      // conformant "1:1" default now reaches it, so guard the array source.
      (currentDocument.annotationScales ?? []);
  const importedNameInCatalogue =
    !!importedDocument.cannoscaleName &&
    mergedAnnotationScales.some((s) => s.name === importedDocument.cannoscaleName);
  const mergedCannoscaleName = importedNameInCatalogue
    ? importedDocument.cannoscaleName
    : importedHasScales
      ? (importedDocument.cannoscaleName ??
          mergedAnnotationScales[0]?.name ??
          currentDocument.cannoscaleName)
      : currentDocument.cannoscaleName;
  const mergedCannoscale = importedNameInCatalogue
    ? importedDocument.cannoscale
    : importedHasScales
      ? (mergedAnnotationScales.find((s) => s.name === mergedCannoscaleName)
          ?.scaleFactor ??
        importedDocument.cannoscale ??
        currentDocument.cannoscale)
      : currentDocument.cannoscale;

  const mergedSpaces = Array.from(spacesById.values());
  const mergedLayoutsBySpaceId: Record<string, Layout> = {};
  for (const space of mergedSpaces) {
    if (space.kind === "layout" && mergedLayouts[space.name]) {
      mergedLayoutsBySpaceId[space.id] = mergedLayouts[space.name] as Layout;
    }
  }
  const mergedViewportsBySpaceId: Record<string, ViewportEntity[]> = {};
  for (const space of mergedSpaces) {
    const vps = space.entities.filter(
      (e): e is ViewportEntity => e.type === "Viewport",
    );
    if (vps.length > 0) mergedViewportsBySpaceId[space.id] = vps;
  }

  return assembleDrawingDocument({
    ...currentDocument,
    blocks: mergedBlocks,
    blockRecords: mergedBlockRecords,
    layouts: mergedLayouts,
    layoutsBySpaceId: mergedLayoutsBySpaceId,
    viewportsBySpaceId: mergedViewportsBySpaceId,
    styleTables: mergedStyleTables,
    insunits: mergedInsunits,
    pdmode: mergedPdmode,
    pdsize: mergedPdsize,
    mirrtext: mergedMirrtext,
    osmode: mergedOsmode,
    cecolor: mergedCeColor,
    spaces: mergedSpaces,
    activeSpaceId: spacesById.has(currentDocument.activeSpaceId)
      ? currentDocument.activeSpaceId
      : importedDocument.activeSpaceId,
    cannoscaleName: mergedCannoscaleName,
    cannoscale: mergedCannoscale,
    annotationScales: mergedAnnotationScales,
    annoAllVisible: importedDocument.annoAllVisible ?? currentDocument.annoAllVisible,
    annoAutoScale: importedDocument.annoAutoScale ?? currentDocument.annoAutoScale,
    annoMonitor: importedDocument.annoMonitor ?? currentDocument.annoMonitor,
    savedView: currentDocument.savedView,
    // Codex round-2 P1 — take the imported marker ONLY on an empty-seed REPLACE
    // (the result BECOMES the imported doc) OR when the host is ALREADY
    // IMPORTED-SOURCE. On INSERT into a from-scratch host the result must KEEP the
    // host's provenance (undefined ⇒ stays from-scratch ⇒ gets the skeleton + hex
    // handles on save); blindly taking the marker mis-marked it imported → skeleton
    // skipped → OLD non-hex/minimal DXF (#384). round-17 BUG 1 (per-SITE sweep) —
    // gate on `isImportedSourceDocument` so a MARKER-DROPPED raw-section host
    // carries its provenance consistently.
    provenance:
      currentIsEmptySeed || isImportedSourceDocument(currentDocument)
        ? importedDocument.provenance ?? currentDocument.provenance
        : currentDocument.provenance,
  });
}

export function mergeImportedGeometryDocument(
  currentDocument: DrawingDocument,
  importedDocument: DrawingDocument,
  // Codex #343 round-1 FINDING 1 — explicit empty-seed gate for the $CECOLOR
  // merge. The sync `importDxfText` path ALWAYS merges (for both empty-seed OPEN
  // and INSERT), so it passes its already-computed `currentIsEmptySeedSync` so
  // an INSERT into a non-empty doc preserves the host $CECOLOR (mirrors the
  // MIRRTEXT/OSMODE hydration gate). Optional with a pure entity-presence
  // default for test callers.
  currentIsEmptySeed: boolean = documentHasNoInlineEntities(currentDocument),
): DrawingDocument {
  // Merge document-level dictionaries (blocks, blockRecords, layouts,
  // styleTables, insunits) from the imported document into the current
  // one. Without this step the DXF import drops all imported BLOCK
  // definitions, which leaves INSERT entities pointing at block names
  // that have no entry in `document.blocks` — DDATTE / EATTEDIT /
  // BEDIT / WBLOCK / RENAME / PURGE all gate on the block-definition
  // lookup and silently no-op when it returns undefined.
  //
  // Conflict policy: imported entries WIN over current entries. The
  // current document at import time is typically the empty-doc seed.
  const mergedBlocks = {
    ...(currentDocument.blocks ?? {}),
    ...(importedDocument.blocks ?? {}),
  };
  const mergedBlockRecords = {
    ...(currentDocument.blockRecords ?? {}),
    ...(importedDocument.blockRecords ?? {}),
  };
  const mergedLayouts = {
    ...(currentDocument.layouts ?? {}),
    ...(importedDocument.layouts ?? {}),
  };
  const mergedStyleTables = (() => {
    const cur = currentDocument.styleTables ?? {};
    const imp = importedDocument.styleTables ?? {};
    return {
      ...cur,
      ...imp,
      // Style sub-tables are arrays of named records — concat then
      // dedupe-by-name with imported winning.
      dimensionStyles: dedupeStylesByName(
        cur.dimensionStyles,
        imp.dimensionStyles,
      ),
      textStyles: dedupeStylesByName(cur.textStyles, imp.textStyles),
      multileaderStyles: dedupeStylesByName(
        cur.multileaderStyles,
        imp.multileaderStyles,
      ),
    };
  })();
  const mergedInsunits = mergeDefaultZeroVar(
    currentDocument.insunits,
    importedDocument.insunits,
  );
  // $PDMODE / $PDSIZE follow the SAME default-0 merge rule as $INSUNITS — the
  // synchronous importDxfText path always merges into the empty seed, so
  // without this the `...currentDocument` spread drops imported PDMODE/PDSIZE
  // before export (Codex #248 F3).
  const mergedPdmode = mergeDefaultZeroVar(
    currentDocument.pdmode,
    importedDocument.pdmode,
  );
  const mergedPdsize = mergeDefaultZeroVar(
    currentDocument.pdsize,
    importedDocument.pdsize,
  );
  // $MIRRTEXT — same default-0 merge as $INSUNITS/$PDMODE/$PDSIZE (see the
  // async merge path above for the full rationale).
  const mergedMirrtext = mergeDefaultZeroVar(
    currentDocument.mirrtext,
    importedDocument.mirrtext,
  );
  // $OSMODE — non-zero default (4133), parameterised `mergeDefaultVar` (see the
  // async merge path above for the full rationale; #342 F3).
  const mergedOsmode = mergeDefaultVar(
    currentDocument.osmode,
    importedDocument.osmode,
    OSMODE_DEFAULT,
  );
  // $CECOLOR — typed union (see the async merge path above for the full
  // rationale): empty-seed OPEN takes imported; live INSERT keeps live colour.
  // Gated on the EXPLICIT empty-seed flag (FINDING 1), not the ByLayer proxy.
  const mergedCeColor = mergeCeColor(
    currentDocument,
    importedDocument.cecolor,
    currentIsEmptySeed,
  );

  // Merge spaces + entities without re-parsing schema inside the loop.
  // Previous impl was O(N²) × schema.parse — catastrophic on 11 MB
  // real-world DXF with thousands of entities and hundreds of blocks.
  // We now compute the fully-merged plain object and parse exactly once
  // at the end. Dropped import time from >30 s to ~4 s on АБИ1.dxf.
  const spacesById = new Map<string, (typeof currentDocument.spaces)[number]>();
  for (const space of currentDocument.spaces) {
    spacesById.set(space.id, { ...space, entities: [...space.entities] });
  }
  // Fast path for entity-id uniqueness: build once, grow incrementally.
  //
  // BUG 4 (round-14 P2) — DXF handles are CASE-INSENSITIVE. Seed the existing
  // host entity ids LOWERCASED so the collision probe normalizes BOTH sides: an
  // imported host entity "ABC" + an inserted "abc" (same handle, different case)
  // is a real case-insensitive duplicate, and the pass-through writer would emit
  // two group-5s that ezdxf reads as one (the inserted one MUST be renamed). The
  // structural seed (`seedStructuralHostHandles` → `collectHandlesDeep`) already
  // lowercases its handles; lowercasing the entity-id population keeps the WHOLE
  // avoidance set case-normalized so `has(idLower)` catches every collision.
  const usedEntityIds = new Set<string>();
  for (const space of spacesById.values()) {
    for (const e of space.entities) usedEntityIds.add(e.id.toLowerCase());
  }

  // BUG 1 (round-11 P2) — same as the async path: a collision-rename must mint a
  // HEX id when the merged result will be emitted as an IMPORTED (pass-through)
  // document (INSERT-into-imported or empty-seed OPEN), so the writer never emits
  // a non-hex `entity.id` as a group-5 handle (strict-ezdxf `Invalid handle`).
  //
  // round-17 BUG 1 (per-SITE sweep) — gate on `isImportedSourceDocument` (mirror the
  // async path so a MARKER-DROPPED raw-section host takes the HEX-mint branch).
  const resultIsImportedSync =
    currentIsEmptySeed || isImportedSourceDocument(currentDocument);

  // BUG 2 (round-12 P2) — mirror the async path: seed the structural host handle
  // population so a minted hex collision-rename id never aliases a STRUCTURAL host
  // handle emitted verbatim in the pass-through document.
  if (resultIsImportedSync) {
    seedStructuralHostHandles(currentDocument, usedEntityIds);
    seedStructuralHostHandles(importedDocument, usedEntityIds);
  }

  // round-16 BUG 1 — mirror the async path: accumulate oldId(lower) → newId so
  // same-batch sibling refs (HATCH sourceBoundaryHandles, viewport
  // clippingBoundaryHandle, MLEADER attDefHandle, dimension/reactor refs, XData
  // 1005) FOLLOW a collision-rename instead of dangling / resolving to the host's
  // same-spelled handle. Only the IMPORTED entities are rewritten (host entities
  // keep their own refs).
  const importedRenameMapSync = new Map<string, string>();
  const importedEntitiesForRemapSync: DrawingDocument["spaces"][number]["entities"][number][] =
    [];
  for (const importedSpace of importedDocument.spaces) {
    const target =
      spacesById.get(importedSpace.id) ??
      (() => {
        const fresh = { ...importedSpace, entities: [] };
        spacesById.set(importedSpace.id, fresh);
        return fresh;
      })();
    for (const importedEntity of importedSpace.entities) {
      let entity = importedEntity;
      // BUG 3 (round-13) — DXF handles are CASE-INSENSITIVE. `seedStructuralHost
      // Handles` (→ `collectHandlesDeep`) seeds every structural host handle
      // LOWERCASED, but `importedEntity.id` is the verbatim source id (may be
      // upper-case hex, e.g. "10000A"). BUG 4 (round-14) — the EXISTING host
      // entity ids are now ALSO seeded lowercased, so the avoidance set is fully
      // case-normalized. Probe the LOWERCASE id, which catches a case-insensitive
      // collision against BOTH a structural host handle ("10000a") AND an existing
      // host entity id ("abc" vs "ABC") that the pass-through writer would emit as
      // a duplicate group-5 (strict-ezdxf reject).
      const idLower = importedEntity.id.toLowerCase();
      if (usedEntityIds.has(idLower)) {
        // Collision — for a from-scratch host keep the load-bearing dash-id
        // (`"line-2"` etc, re-minted to hex later); for an imported host mint a
        // fresh HEX id so the pass-through writer emits a valid group-5 (BUG 1).
        const candidate = mintCollisionRenameId(
          importedEntity.type,
          usedEntityIds,
          resultIsImportedSync,
        );
        entity = { ...importedEntity, id: candidate };
        // round-16 BUG 1 — record old→new so same-batch sibling refs follow.
        importedRenameMapSync.set(idLower, candidate);
      }
      // Re-add the kept/renamed id LOWERCASED so the set stays case-normalized for
      // subsequent inserts (a later "ABC" must collide with this "abc").
      usedEntityIds.add(entity.id.toLowerCase());
      target.entities.push(entity);
      importedEntitiesForRemapSync.push(entity);
    }
  }
  // round-16 BUG 1 — rewrite each IMPORTED entity's reference fields through the
  // accumulated rename map (no-op when nothing was renamed). The own `id` is
  // excluded by `remapEntityHandleReferences` (never remaps a bare own handle).
  if (importedRenameMapSync.size > 0) {
    for (const entity of importedEntitiesForRemapSync) {
      remapEntityHandleReferences(entity, importedRenameMapSync);
    }
  }

  // ── Annotation header / catalogue (PR-3) ──────────────────────────
  // The current document at import time is the empty seed which carries
  // schema defaults (`cannoscaleName: "1:1"`, `cannoscale: 1`,
  // `annotationScales: []`, `annoAllVisible: true`, `annoAutoScale: 4`,
  // `annoMonitor: false`). The imported document is what the bridge just
  // populated from the DXF HEADER + OBJECTS sections. Without these
  // overrides the spread of `...currentDocument` below would clobber the
  // imported values back to defaults — CANNOSCALE switch in the status
  // bar would then have nothing to drive (annotationScales: []) and
  // every annotative-context lookup would fall back to "1:1". Bug
  // confirmed live on АБИ1.dxf (19 scales documented, 0 reaching state).
  //
  // Conflict policy: imported wins when present. We treat
  // `annotationScales: []` as "not set" so a current doc with a real
  // catalogue is not wiped by a re-import that has none. The header
  // fields fall back per-key — empty imported defaults do not clobber
  // an existing real selection.
  const importedHasScales =
    Array.isArray(importedDocument.annotationScales) &&
    importedDocument.annotationScales.length > 0;
  const mergedAnnotationScales = importedHasScales
    ? importedDocument.annotationScales
    : // `?? []` — a host with no annotation catalogue (undefined) must not crash
      // the `.some` membership probe below. Previously masked because the import
      // left an EMPTY cannoscaleName (falsy → `&&` short-circuited the probe); the
      // conformant "1:1" default now reaches it, so guard the array source.
      (currentDocument.annotationScales ?? []);
  // Header pair — prefer imported when its name appears in the merged
  // catalogue, otherwise keep current. This protects against a degenerate
  // imported header that names a scale not in the catalogue.
  const importedNameInCatalogue =
    !!importedDocument.cannoscaleName &&
    mergedAnnotationScales.some(
      (s) => s.name === importedDocument.cannoscaleName,
    );
  const mergedCannoscaleName = importedNameInCatalogue
    ? importedDocument.cannoscaleName
    : importedHasScales
      ? // Imported brought a catalogue but no usable name — pick the
        // first catalogue entry (matches AutoCAD's behaviour of opening
        // a drawing on its CANNOSCALE header value or first scale).
        (importedDocument.cannoscaleName ??
          mergedAnnotationScales[0]?.name ??
          currentDocument.cannoscaleName)
      : currentDocument.cannoscaleName;
  const mergedCannoscale = importedNameInCatalogue
    ? importedDocument.cannoscale
    : importedHasScales
      ? (mergedAnnotationScales.find((s) => s.name === mergedCannoscaleName)
          ?.scaleFactor ??
        importedDocument.cannoscale ??
        currentDocument.cannoscale)
      : currentDocument.cannoscale;

  // Single assembleDrawingDocument pass at the end — Zod parse runs only
  // in dev/test (catching bugs); prod uses validateDrawingModel invariants
  // check + cast which preserves entity reference identity.
  const syncMergedSpaces = Array.from(spacesById.values());
  const syncMergedLayoutsBySpaceId: Record<string, Layout> = {};
  for (const space of syncMergedSpaces) {
    if (space.kind === "layout" && mergedLayouts[space.name]) {
      syncMergedLayoutsBySpaceId[space.id] = mergedLayouts[space.name] as Layout;
    }
  }
  const syncMergedViewportsBySpaceId: Record<string, ViewportEntity[]> = {};
  for (const space of syncMergedSpaces) {
    const vps = space.entities.filter(
      (e): e is ViewportEntity => e.type === "Viewport",
    );
    if (vps.length > 0) syncMergedViewportsBySpaceId[space.id] = vps;
  }
  return assembleDrawingDocument({
    ...currentDocument,
    blocks: mergedBlocks,
    blockRecords: mergedBlockRecords,
    layouts: mergedLayouts,
    layoutsBySpaceId: syncMergedLayoutsBySpaceId,
    viewportsBySpaceId: syncMergedViewportsBySpaceId,
    styleTables: mergedStyleTables,
    insunits: mergedInsunits,
    pdmode: mergedPdmode,
    pdsize: mergedPdsize,
    mirrtext: mergedMirrtext,
    osmode: mergedOsmode,
    cecolor: mergedCeColor,
    spaces: syncMergedSpaces,
    activeSpaceId: spacesById.has(currentDocument.activeSpaceId)
      ? currentDocument.activeSpaceId
      : importedDocument.activeSpaceId,
    // Annotation header + catalogue (see comment block above).
    cannoscaleName: mergedCannoscaleName,
    cannoscale: mergedCannoscale,
    annotationScales: mergedAnnotationScales,
    annoAllVisible:
      importedDocument.annoAllVisible ?? currentDocument.annoAllVisible,
    annoAutoScale:
      importedDocument.annoAutoScale ?? currentDocument.annoAutoScale,
    annoMonitor:
      importedDocument.annoMonitor ?? currentDocument.annoMonitor,
    // INSERT semantics: the host's view MUST NOT be altered by an
    // imported file. Always keep `currentDocument.savedView`, even when
    // it is undefined (host had no saved view → host stays with no
    // saved view → camera does not jump on INSERT).
    //
    // OPEN semantics (replace, not merge) is handled separately in
    // editorStore.ts where `mergedGeometryDocument.savedView` is
    // explicitly overridden with `importedGeometryDocument.savedView`
    // after this merge returns.
    savedView: currentDocument.savedView,
    // Codex round-2 P1 — the SYNC merge serves BOTH empty-seed OPEN/replace AND
    // INSERT (unlike the async paths). Take the imported marker ONLY on an
    // empty-seed REPLACE OR when the host is ALREADY IMPORTED-SOURCE; on INSERT into
    // a from-scratch host the result must KEEP the host's provenance (⇒ stays
    // from-scratch ⇒ gets the skeleton + hex handles on save) — blindly carrying the
    // marker skipped the skeleton → OLD non-hex/minimal DXF (#384). round-17 BUG 1
    // (per-SITE sweep) — gate on `isImportedSourceDocument` so a MARKER-DROPPED
    // raw-section host carries its provenance consistently (mirror the async path).
    provenance:
      currentIsEmptySeed || isImportedSourceDocument(currentDocument)
        ? importedDocument.provenance ?? currentDocument.provenance
        : currentDocument.provenance,
  });
}

// Helper: dedupe a concat of two style arrays by `name`, with the
// second array winning on collisions. Returns undefined when both
// inputs are absent so the caller can omit the field cleanly.
function dedupeStylesByName<T extends { name: string }>(
  current: T[] | undefined,
  imported: T[] | undefined,
): T[] | undefined {
  if (!current && !imported) return undefined;
  const map = new Map<string, T>();
  for (const s of current ?? []) map.set(s.name, s);
  for (const s of imported ?? []) map.set(s.name, s);
  return Array.from(map.values());
}

export function syncShellStateFromMergedGeometryDocument(
  state: EditorShellState,
  geometryDocument: DrawingDocument,
  options?: { assumeValid?: boolean },
): EditorShellState {
  const nextState = syncShellStateFromGeometryDocument(
    state,
    geometryDocument,
    options,
  );
  const preservedNonGeometryEntities = state.drawing.entities.filter(
    (entity) => !isGeometryBackedPreviewEntity(entity),
  );

  // Fast path: skip the second `editorShellStateSchema.parse`. `nextState` was
  // just validated by the inner `syncShellStateFromGeometryDocument` (ends with
  // `.parse`) and `preservedNonGeometryEntities` came from the already-validated
  // `state.drawing.entities`; re-parsing would re-walk every entity (doubling the
  // sync cost on 5000+-entity merges) for no new invariant. The only mutation vs
  // `nextState` is the entities-array concat (EntityPreview[] preserved).
  //
  // Phase 3b PR #6 — explicitly wipe selection: an import-time merge replaces the
  // whole geometry document, so any prior selection is stale. `previousPickset` is
  // a `.default([])` (required on the parsed type) and this fast path skips the
  // re-parse, so spell both selection fields out explicitly.
  const merged: EditorShellState = {
    ...nextState,
    drawing: {
      ...nextState.drawing,
      entities: [
        ...nextState.drawing.entities,
        ...preservedNonGeometryEntities,
      ],
    },
    selection: { entityIds: [], previousPickset: [] },
    propertyWorkflows: {
      ...nextState.propertyWorkflows,
      matchpropSourceEntityId: null,
    },
  };
  // @codex #499 P2 — this helper PRESERVES `propertyWorkflows` (incl. the current
  // DIMSTYLE pointer). An OPEN / import MERGE swaps or unions the dimension-style
  // table, so a pointer from the prior drawing can strand at a style the merged
  // document lacks (the create-dimension stamp then names a phantom style).
  // Reconcile it — the OPEN-path sibling of `reconcileCurrentDimStyleAgainstTable`.
  return reconcileCurrentDimStyleAgainstMergedDocument(merged, geometryDocument);
}


// T7 (agent-bridge), file-size split — `resolveGeometryDocumentCommand`
// (the dispatch-router allowlist function) moved to `storeHelpersResolveCommand.ts`
// to bring this file back under the CLAUDE.md 2000-line ceiling. Re-exported
// here so every existing `import { resolveGeometryDocumentCommand } from
// "./storeHelpers"` call site keeps working unchanged.
export { resolveGeometryDocumentCommand } from "./storeHelpersResolveCommand";
